import { Object3D, Vector3 } from "three";

import { Context } from "../../context";
import { Frames } from "../../util/frames";

import { Actor } from "./actor";
import { RawHelper } from "./helpers/raw-helper";
import { poserPlayerTypeFromMannequin } from "../../../lib/misc/player-type";
import { PlayerType } from "mannequin/src/types/player";

export class Actors extends Object3D {
  private actors: Map<number, Actor> = new Map();

  private maxActorAge = 5;
  private prevFrameNum = -1;

  private contextRef: Context;

  private debugHawkeye: Map<number, RawHelper> = new Map();
  private homePlate = new Vector3(0, 0, 0);

  constructor(context: Context) {
    super();

    this.contextRef = context;
  }

  update(frames: Frames, time: number) {
    const labels = this.contextRef.bufferManager.labels;
    let frame = frames.current;

    if (labels && frame != undefined && this.prevFrameNum != frame.num) {
      // add actors and update we see
      for (const pose of frame.actorPoses) {
        const uid = pose.uid;
        let existing = this.actors.get(uid);
        const label = labels[uid];

        if (!existing && label) {
          const { actor, type } = label;
          // TODO: put this into actor constructor
          const player = this.contextRef.gameState.getPlayerByActorId(actor);
          existing = new Actor(this.contextRef, {
            type: poserPlayerTypeFromMannequin(type),
            uid,
            actorId: actor,
            player,
          });
          if (existing) {
            this.actors.set(uid, existing);
            this.add(existing);
          } else {
            console.error(`failure to make actor for position ${uid}`);
          }
        }
      }

      this.updateDebug(frames, time);

      this.prevFrameNum = frame.num;
    }

    for (const [uid, actor] of this.actors.entries()) {
      actor.update(frames, time);

      if (frame) {
        const age = actor.ageFrom(frame.num);
        if (age > this.maxActorAge || age < 0) {
          // TODO: transition actors away rather than hard delete here
          this.remove(actor);
          this.actors.delete(uid);
        }
      }
    }
  }

  private updateDebug(frames: Frames, time: number) {
    // add debug raw centroids
    const frame = frames.current;
    if (!frame) return;

    if (this.contextRef.state.trackingDebugEnabled) {
      if (frame.rawJoints) {
        for (const position of frame.rawJoints) {
          let existing = this.debugHawkeye.get(position.track_id);
          if (!existing) {
            existing = new RawHelper(position, this.contextRef);
            if (existing) {
              console.warn(`adding centroid for ${position.track_id}`);
              this.debugHawkeye.set(position.track_id, existing);

              existing.visible = this.contextRef.state.trackingDebugEnabled;

              this.add(existing);
            } else {
              console.error(
                `failure to make centroid for position ${position.track_id}`
              );
            }
          }
        }
      }
    } else {
      for (const helper of this.debugHawkeye.values()) {
        helper.visible = this.contextRef.state.trackingDebugEnabled;
      }
    }

    for (const [trackId, centroid] of this.debugHawkeye.entries()) {
      centroid.update(frames, time);

      if (centroid.ageFrom(frame.num) > 5) {
        this.remove(centroid);
        this.debugHawkeye.delete(trackId);
      }
    }
  }

  getActors(): IterableIterator<Actor> {
    return this.actors.values();
  }

  getActor(posId: number): Actor | undefined {
    // TODO: decide if we want to key this on actorId instead
    return [...this.actors.values()].find((a) => a.positionId === posId);
  }

  getBatter(): Actor | undefined {
    const batter = this.getActor(10);
    if (
      batter &&
      batter.centroid.position.distanceToSquared(this.homePlate) < 100
    ) {
      return batter;
    } else {
      return undefined;
    }
  }

  getHawkeyes(): IterableIterator<RawHelper> {
    return this.debugHawkeye.values();
  }
}
